import React, { useState } from "react";
import { SmartGoal } from "src/models/form-types";
import { FormTextarea } from "./FormTextarea";
import "../../../styles/components/forms/smart-goal-selector.css";
import { useFunnyMessages } from "src/hooks/useFunnyMessages";
import { CollapsibleFormSection } from "./CollapsibleFormSection";

interface FormValidation {
  isPurposeValid: boolean;
  isLearningSupportValid?: boolean;
  isLearningValid?: boolean; // For PLASP forms
  isAdditionalInfoValid: boolean;
  isLevelOfAdjustmentValid?: boolean;
  isAdjustmentLevelValid?: boolean; // For PLASP forms
  isBehavioursValid?: boolean; // Optional for PLASP
  isBehaviourContextValid?: boolean; // Optional for PLASP
  isBehaviourFunctionValid?: boolean; // Optional for PLASP
  isStudentInfoValid?: boolean; // For PLASP forms
}

interface SmartGoalSelectorProps {
  goals: SmartGoal[];
  isLoading: boolean;
  error: string | null;
  onGenerateGoals: () => Promise<void>;
  onGoalSelect: (goal: string) => void;
  isFormValid: boolean;
  formValidation: FormValidation;
  radioGroupName?: string;
  className?: string;
  invalidHintTitle?: string;
  invalidHintItems?: string[];
}

export const SmartGoalSelector: React.FC<SmartGoalSelectorProps> = ({
  goals,
  isLoading,
  error,
  onGenerateGoals,
  onGoalSelect,
  isFormValid,
  formValidation,
  radioGroupName = "selectedGoal",
  className,
  invalidHintTitle,
  invalidHintItems,
}) => {
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [customGoal, setCustomGoal] = useState("");
  const [showGoals, setShowGoals] = useState(false);

  const funnyMessage = useFunnyMessages(isLoading);

  const handleGoalSelect = (goalId: number) => {
    setSelectedGoalId(goalId);
    setCustomGoal("");

    const selectedGoal = goals.find((goal) => goal.id === goalId);
    if (selectedGoal) {
      onGoalSelect(selectedGoal.text);
    }
  };

  const handleCustomGoalChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setCustomGoal(e.target.value);
    setSelectedGoalId(null);
    onGoalSelect(e.target.value);
  };

  const handleGenerateClick = async () => {
    if (isFormValid) {
      setShowGoals(true);
      await onGenerateGoals();
    }
  };

  return (
    <CollapsibleFormSection
      title="Generate SMART Goals *"
      className={className}
    >
      {/* Removed explanatory text per request */}

      {!showGoals && (
        <div className="form-group center-button-container">
          <div className="generate-button-container">
            <div className="tooltip-container">
              <button
                type="button"
                className="primary-button"
                onClick={handleGenerateClick}
                disabled={isLoading || !isFormValid}
              >
                {isLoading ? funnyMessage : "Generate SMART Goals"}
              </button>

              {!isFormValid && (
                <div className="tooltip">
                  <p>
                    {invalidHintTitle ??
                      "Please complete these required fields:"}
                  </p>
                  <div className="tooltip-items">
                    {invalidHintItems && invalidHintItems.length > 0 ? (
                      invalidHintItems.map((item, idx) => (
                        <div key={idx} className="tooltip-item">
                          • {item}
                        </div>
                      ))
                    ) : (
                      <>
                        {!formValidation.isPurposeValid && (
                          <div className="tooltip-item">• Purpose</div>
                        )}
                        {formValidation.isLearningSupportValid !== undefined &&
                          !formValidation.isLearningSupportValid && (
                            <div className="tooltip-item">
                              • Learning Support
                            </div>
                          )}
                        {formValidation.isLearningValid !== undefined &&
                          !formValidation.isLearningValid && (
                            <div className="tooltip-item">
                              • Learning & Support Information
                            </div>
                          )}
                        {!formValidation.isAdditionalInfoValid && (
                          <div className="tooltip-item">
                            • Additional Information
                          </div>
                        )}
                        {formValidation.isLevelOfAdjustmentValid !==
                          undefined &&
                          !formValidation.isLevelOfAdjustmentValid && (
                            <div className="tooltip-item">
                              • Level of Adjustment
                            </div>
                          )}
                        {formValidation.isAdjustmentLevelValid !== undefined &&
                          !formValidation.isAdjustmentLevelValid && (
                            <div className="tooltip-item">
                              • Level of Adjustment
                            </div>
                          )}
                        {formValidation.isStudentInfoValid !== undefined &&
                          !formValidation.isStudentInfoValid && (
                            <div className="tooltip-item">
                              • Student Information
                            </div>
                          )}
                        {formValidation.isBehavioursValid !== undefined &&
                          !formValidation.isBehavioursValid && (
                            <div className="tooltip-item">• Behaviours</div>
                          )}
                        {formValidation.isBehaviourContextValid !== undefined &&
                          !formValidation.isBehaviourContextValid && (
                            <div className="tooltip-item">
                              • Behaviour Context
                            </div>
                          )}
                        {formValidation.isBehaviourFunctionValid !==
                          undefined &&
                          !formValidation.isBehaviourFunctionValid && (
                            <div className="tooltip-item">
                              • Behaviour Function
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="form-error-message">
          <p>Error generating SMART goals: {error}</p>
        </div>
      )}

      {showGoals && (
        <div className="smart-goals-container">
          {isLoading ? (
            <div className="loading-indicator">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeDasharray="32"
                  strokeDashoffset="10"
                />
              </svg>
              <span>{funnyMessage}</span>
            </div>
          ) : (
            <>
              <h3 className="section-title">
                Select a SMART Goal or Write Your Own
              </h3>

              {goals.length > 0 ? (
                <div className="form-group">
                  {goals.map((goal) => (
                    <div
                      key={goal.id}
                      className={`goal-card ${
                        selectedGoalId === goal.id ? "selected" : ""
                      }`}
                      onClick={() => handleGoalSelect(goal.id)}
                    >
                      <input
                        type="radio"
                        id={`goal-${goal.id}`}
                        name={radioGroupName}
                        checked={selectedGoalId === goal.id}
                        onChange={() => handleGoalSelect(goal.id)}
                        className="goal-radio-hidden"
                      />
                      <div className="goal-content">
                        <span className="goal-text">{goal.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="form-note">
                  No SMART goals were generated. Try again or write your own
                  goal below.
                </p>
              )}

              <div className="form-group custom-smart-goal">
                <h4 className="custom-goal-title">
                  Not quite right? Edit one of ours or write your own here:
                </h4>
                <FormTextarea
                  id="customSmartGoal"
                  name="customSmartGoal"
                  label=""
                  value={customGoal}
                  onChange={handleCustomGoalChange}
                  rows={3}
                  placeholder="Enter your custom SMART goal here..."
                />
              </div>

              <div className="form-group">
                <div className="tooltip-container">
                  <button
                    type="button"
                    className="secondary-button regenerate-button"
                    onClick={onGenerateGoals}
                    disabled={isLoading || !isFormValid}
                  >
                    Regenerate Goals
                  </button>

                  {!isFormValid && (
                    <div className="tooltip">
                      <p>Please complete these required fields:</p>
                      <div className="tooltip-items">
                        {!formValidation.isPurposeValid && (
                          <div className="tooltip-item">• Purpose</div>
                        )}
                        {formValidation.isLearningSupportValid !== undefined &&
                          !formValidation.isLearningSupportValid && (
                            <div className="tooltip-item">
                              • Learning Support
                            </div>
                          )}
                        {formValidation.isLearningValid !== undefined &&
                          !formValidation.isLearningValid && (
                            <div className="tooltip-item">
                              • Learning & Support Information
                            </div>
                          )}
                        {!formValidation.isAdditionalInfoValid && (
                          <div className="tooltip-item">
                            • Additional Information
                          </div>
                        )}
                        {formValidation.isLevelOfAdjustmentValid !==
                          undefined &&
                          !formValidation.isLevelOfAdjustmentValid && (
                            <div className="tooltip-item">
                              • Level of Adjustment
                            </div>
                          )}
                        {formValidation.isAdjustmentLevelValid !== undefined &&
                          !formValidation.isAdjustmentLevelValid && (
                            <div className="tooltip-item">
                              • Level of Adjustment
                            </div>
                          )}
                        {formValidation.isStudentInfoValid !== undefined &&
                          !formValidation.isStudentInfoValid && (
                            <div className="tooltip-item">
                              • Student Information
                            </div>
                          )}
                        {formValidation.isBehavioursValid !== undefined &&
                          !formValidation.isBehavioursValid && (
                            <div className="tooltip-item">• Behaviours</div>
                          )}
                        {formValidation.isBehaviourContextValid !== undefined &&
                          !formValidation.isBehaviourContextValid && (
                            <div className="tooltip-item">
                              • Behaviour Context
                            </div>
                          )}
                        {formValidation.isBehaviourFunctionValid !==
                          undefined &&
                          !formValidation.isBehaviourFunctionValid && (
                            <div className="tooltip-item">
                              • Behaviour Function
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </CollapsibleFormSection>
  );
};
